import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
// html2pdf.js entfernt - wir nutzen jetzt Typst Backend für PDF-Generierung
import { format, parse, isValid } from "date-fns";
import { de } from "date-fns/locale";
import { AuthProvider, UserMenu, getAccessToken, getIdToken } from "@/components/AuthProvider";
import {
  FileText, CheckCircle2, Calculator, Loader2,
  FileSpreadsheet, Printer, RotateCcw, Info, Upload, Eye,
  FolderOpen, Plus, Trash2, Calendar as CalendarIcon, User, AlertTriangle, Check, X, Edit, Save,
  Download, Mail, Cloud, Send, Bot, Sparkles, FilePlus, Scale
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Konstanten aus separatem Modul importieren
import { PFLEGEGRADE, LK_KATALOG, LK_KATEGORIEN } from "@/constants/lk-katalog";
// Custom Hooks
import { usePdfGenerator } from "@/hooks/usePdfGenerator";
// Invoice Components
import { PrintableInvoice } from "@/components/invoice";
// UI Custom Components
import { DatePickerField, FileDropzone } from "@/components/ui-custom";
// Email Components
import { EmailDialog } from "@/components/email";
// Error Boundary
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy-loaded Pages for Code Splitting (40-60% smaller initial bundle)
const AntragsmanagementDashboard = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.Dashboard })));
const KlientenAkte = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.KlientenAkte })));
const BewilligungsListe = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.BewilligungsListe })));
const Widerspruchsmanagement = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.Widerspruchsmanagement })));
const WiderspruchAgent = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.WiderspruchAgent })));
const AgententeamPage = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.AgententeamPage })));
const Antragsstellung = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.Antragsstellung })));
const WiderspruchWorkflow = lazy(() => import("@/pages/antragsmanagement").then(m => ({ default: m.WiderspruchWorkflow })));
const AnwaltsPortal = lazy(() => import("@/pages/anwaltsportal").then(m => ({ default: m.AnwaltsPortal })));
const KorrekturrechnungPage = lazy(() => import("@/pages/KorrekturrechnungPage"));
const VergütungsvereinbarungenPage = lazy(() => import("@/pages/VergütungsvereinbarungenPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor: attach MSAL ID token to all backend API requests
axios.interceptors.request.use(async (config) => {
  if (config.url && config.url.startsWith(BACKEND_URL)) {
    try {
      const token = await getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn("Failed to acquire token for API request:", e);
    }
  }
  return config;
});

// Build-Zeitstempel (wird beim GitHub Actions Build gesetzt)
const BUILD_TIMESTAMP = process.env.REACT_APP_BUILD_TIMESTAMP || "Lokal";

// ============================================================================
// NAVIGATION SIDEBAR
// ============================================================================

const Sidebar = ({ activeTab, onTabChange }) => {
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: FileText, description: "Übersicht" },
    { id: "klienten", label: "Klienten", icon: User, description: "Klienten-Akte" },
    { id: "antragsstellung", label: "Antragsstellung", icon: FilePlus, description: "Erst- & Folgeanträge" },
    { id: "bewilligungen", label: "Bewilligungen", icon: FileSpreadsheet, description: "Alle Bewilligungen" },
    { id: "korrekturrechnung", label: "Korrekturrechnung", icon: Calculator, description: "Erstellen" },
    { id: "widerspruch-workflow", label: "Widerspruch", icon: Scale, description: "Neuer Workflow" },
    { id: "widersprueche", label: "Übersicht", icon: AlertTriangle, description: "Alle Widersprüche" },
    { id: "agententeam", label: "3-Agenten KI", icon: Sparkles, description: "Legacy" },
    { id: "vereinbarungen", label: "Vereinbarungen", icon: FolderOpen, description: "Vergütung" },
    { id: "anwaltsportal", label: "Anwalts-Portal", icon: User, description: "Für Rechtsanwälte" },
  ];
  
  return (
    <div className="w-56 bg-white border-r border-slate-200 min-h-[calc(100vh-56px)]">
      <nav className="p-3 space-y-1">
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left ${
                isActive ? "bg-[#1B4965] text-white" : "hover:bg-[#E8F4F8] text-slate-700"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-4 h-4" />
              <div>
                <div className="font-medium text-sm">{item.label}</div>
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================

const DomusVitaApp = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [widerspruchVorlage, setWiderspruchVorlage] = useState(null);
  const [widerspruchResumeId, setWiderspruchResumeId] = useState(null);

  // Navigation zum Widerspruch-Workflow mit vorausgefüllten Daten
  const navigateToWiderspruchWorkflow = (bewilligungData) => {
    setWiderspruchVorlage(bewilligungData);
    setWiderspruchResumeId(null);
    setActiveTab("widerspruch-workflow");
  };

  // Widerspruch-Workflow fortsetzen
  const resumeWiderspruchWorkflow = (widerspruchId) => {
    setWiderspruchVorlage(null);
    setWiderspruchResumeId(widerspruchId);
    setActiveTab("widerspruch-workflow");
  };

  // Legacy: Navigation zum AgententeamPage
  const navigateToWiderspruchAgent = (bewilligungData) => {
    setWiderspruchVorlage(bewilligungData);
    setActiveTab("agententeam");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#1B4965] text-white shadow h-14 flex items-center justify-between px-4 no-print">
        <div className="flex items-center gap-3">
          <img src="/logo.png"
               alt="DomusVita Logo" className="h-10 w-auto" />
          <div>
            <h1 className="text-lg font-bold leading-tight">DomusVita Antragsmanagement</h1>
            <p className="text-xs text-white/70 leading-tight">Stand: {BUILD_TIMESTAMP}</p>
          </div>
        </div>
        <UserMenu />
      </header>

      <div className="flex">
        <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="flex-1">
          <Suspense fallback={
            <div className="flex items-center justify-center h-screen">
              <Loader2 className="w-8 h-8 animate-spin text-[#1B4965]" />
            </div>
          }>
            {activeTab === "dashboard" && <AntragsmanagementDashboard onNavigate={setActiveTab} />}
            {activeTab === "klienten" && <KlientenAkte onNavigate={setActiveTab} onResumeWiderspruch={resumeWiderspruchWorkflow} />}
            {activeTab === "antragsstellung" && <Antragsstellung />}
            {activeTab === "bewilligungen" && <BewilligungsListe onCreateWiderspruch={navigateToWiderspruchWorkflow} />}
            {activeTab === "widerspruch-workflow" && <WiderspruchWorkflow vorlage={widerspruchVorlage} onClearVorlage={() => setWiderspruchVorlage(null)} resumeId={widerspruchResumeId} />}
            {activeTab === "widersprueche" && <Widerspruchsmanagement onResumeWiderspruch={resumeWiderspruchWorkflow} />}
            {activeTab === "agententeam" && <AgententeamPage vorlage={widerspruchVorlage} onClearVorlage={() => setWiderspruchVorlage(null)} />}
            {activeTab === "anwaltsportal" && <AnwaltsPortal />}
            {activeTab === "korrekturrechnung" && <KorrekturrechnungPage />}
            {activeTab === "vereinbarungen" && <VergütungsvereinbarungenPage />}
          </Suspense>
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="*" element={<DomusVitaApp />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
